const PLAIN_META_PREFIX = 'mc:plain:';
const TRACKING_META_PREFIX = 'mc:tracking:';

export type CampaignTrackingSettings = {
  openTracking: boolean;
  clickTracking: boolean;
  utm: {
    source: string;
    medium: string;
    campaign: string;
  };
};

export type CampaignPlainMeta = {
  plainText: string;
  manualPlain: boolean;
};

export const DEFAULT_TRACKING_SETTINGS: CampaignTrackingSettings = {
  openTracking: true,
  clickTracking: true,
  utm: { source: '', medium: 'email', campaign: '' },
};

export const DEFAULT_PLAIN_META: CampaignPlainMeta = {
  plainText: '',
  manualPlain: false,
};

/** Strip HTML to a plain-text alternative (prototype #144 deriveAutoPlainText). */
export const deriveAutoPlainText = (html: string): string =>
  String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>|<\/h[1-6]>|<\/div>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const parseCampaignPlainMeta = (
  tags: string[] | null | undefined
): CampaignPlainMeta => {
  const raw = (tags ?? []).find((tag) => tag.startsWith(PLAIN_META_PREFIX));
  if (!raw) return { ...DEFAULT_PLAIN_META };
  try {
    const parsed = JSON.parse(raw.slice(PLAIN_META_PREFIX.length)) as Partial<CampaignPlainMeta>;
    return {
      plainText: typeof parsed.plainText === 'string' ? parsed.plainText : '',
      manualPlain: Boolean(parsed.manualPlain),
    };
  } catch {
    return { ...DEFAULT_PLAIN_META };
  }
};

export const upsertCampaignPlainMeta = (
  tags: string[] | null | undefined,
  meta: CampaignPlainMeta
): string[] => {
  const without = (tags ?? []).filter((tag) => !tag.startsWith(PLAIN_META_PREFIX));
  if (!meta.manualPlain && !meta.plainText.trim()) {
    return without;
  }
  return [
    ...without,
    `${PLAIN_META_PREFIX}${JSON.stringify({
      plainText: meta.plainText,
      manualPlain: meta.manualPlain,
    } satisfies CampaignPlainMeta)}`,
  ];
};

export const parseCampaignTrackingSettings = (
  tags: string[] | null | undefined
): CampaignTrackingSettings => {
  const raw = (tags ?? []).find((tag) => tag.startsWith(TRACKING_META_PREFIX));
  if (!raw) return { ...DEFAULT_TRACKING_SETTINGS, utm: { ...DEFAULT_TRACKING_SETTINGS.utm } };
  try {
    const parsed = JSON.parse(
      raw.slice(TRACKING_META_PREFIX.length)
    ) as Partial<CampaignTrackingSettings>;
    return {
      openTracking:
        typeof parsed.openTracking === 'boolean'
          ? parsed.openTracking
          : DEFAULT_TRACKING_SETTINGS.openTracking,
      clickTracking:
        typeof parsed.clickTracking === 'boolean'
          ? parsed.clickTracking
          : DEFAULT_TRACKING_SETTINGS.clickTracking,
      utm: {
        source:
          typeof parsed.utm?.source === 'string'
            ? parsed.utm.source
            : DEFAULT_TRACKING_SETTINGS.utm.source,
        medium:
          typeof parsed.utm?.medium === 'string'
            ? parsed.utm.medium
            : DEFAULT_TRACKING_SETTINGS.utm.medium,
        campaign:
          typeof parsed.utm?.campaign === 'string'
            ? parsed.utm.campaign
            : DEFAULT_TRACKING_SETTINGS.utm.campaign,
      },
    };
  } catch {
    return { ...DEFAULT_TRACKING_SETTINGS, utm: { ...DEFAULT_TRACKING_SETTINGS.utm } };
  }
};

export const upsertCampaignTrackingSettings = (
  tags: string[] | null | undefined,
  tracking: CampaignTrackingSettings
): string[] => {
  const without = (tags ?? []).filter((tag) => !tag.startsWith(TRACKING_META_PREFIX));
  return [...without, `${TRACKING_META_PREFIX}${JSON.stringify(tracking)}`];
};
